import { useState } from 'react'
import { Button } from '@diwata/ironvale'
import type { MissionProposal, MissionStep } from '@/api/missions'

// The judgment surface for a machine-drafted mission: retitle, rename/reorder/gate its steps,
// drop the ones you don't want, then create or discard. Shared by the Control Plane queue
// (goal-first creation) and the Signals page (signal → mission). Nothing exists until the
// operator approves — so the operator, not the machine, has the last word on the shape.

export function ProposalCard({
  proposal,
  onCreate,
  onDiscard,
  busy,
}: {
  proposal: MissionProposal
  onCreate: (p: MissionProposal) => void
  onDiscard: () => void
  busy: boolean
}) {
  const [draft, setDraft] = useState(proposal)

  function setSteps(steps: MissionStep[]) {
    setDraft({ ...draft, steps })
  }

  function updateStep(id: string, patch: Partial<MissionStep>) {
    setSteps(draft.steps.map((s) => (s.id === id ? { ...s, ...patch } : s)))
  }

  function removeStep(id: string) {
    if (draft.steps.length <= 2) return // a mission needs at least two steps
    setSteps(draft.steps.filter((s) => s.id !== id))
  }

  function move(index: number, delta: number) {
    const to = index + delta
    if (to < 0 || to >= draft.steps.length) return
    const next = [...draft.steps]
    ;[next[index], next[to]] = [next[to], next[index]]
    setSteps(next)
  }

  function toggleGate(step: MissionStep) {
    updateStep(step.id, { gate: step.gate === 'review' ? 'none' : 'review' })
  }

  // Steps carry their id as their display name; two steps sharing one name would fail the
  // backend's uniqueness check, so guard creation on it and tell the operator why.
  const ids = draft.steps.map((s) => s.id.trim())
  const hasBlank = ids.some((id) => id === '')
  const hasDupes = new Set(ids).size !== ids.length
  const invalid = hasBlank || hasDupes

  return (
    <div className="dw-proposal">
      <div className="dw-rail-section-title">✦ Tala proposes</div>
      <input
        className="dw-input"
        value={draft.title}
        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        aria-label="Mission title"
      />
      <div className="dw-proposal-meta">
        target / {draft.target} · {draft.domain} · {draft.protocol}
      </div>
      <div className="dw-proposal-steps">
        {draft.steps.map((s, i) => (
          <div key={i} className="dw-proposal-step">
            <div className="dw-proposal-step-reorder">
              <button
                type="button"
                className="dw-proposal-step-move"
                aria-label={`Move step ${s.id} up`}
                disabled={i === 0}
                onClick={() => move(i, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                className="dw-proposal-step-move"
                aria-label={`Move step ${s.id} down`}
                disabled={i === draft.steps.length - 1}
                onClick={() => move(i, 1)}
              >
                ↓
              </button>
            </div>
            <input
              className="dw-input dw-proposal-step-input"
              value={s.id}
              aria-label={`Step ${i + 1} name`}
              onChange={(e) => updateStep(s.id, { id: e.target.value })}
            />
            <button
              type="button"
              className={`dw-chip dw-chip--gate dw-proposal-step-gate${
                s.gate === 'review' ? ' dw-proposal-step-gate--on' : ''
              }`}
              aria-pressed={s.gate === 'review'}
              title={s.gate === 'review' ? 'Remove the review gate' : 'Pause here for your review'}
              onClick={() => toggleGate(s)}
            >
              ◇ review
            </button>
            {draft.steps.length > 2 && (
              <button
                type="button"
                className="dw-proposal-step-x"
                aria-label={`Remove step ${s.id}`}
                onClick={() => removeStep(s.id)}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>
      {invalid && (
        <div className="dw-field-error">
          {hasBlank ? 'Every step needs a name.' : 'Step names must be unique.'}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <Button
          size="sm"
          variant="primary"
          disabled={busy || invalid}
          onClick={() => onCreate({ ...draft, steps: draft.steps.map((s) => ({ ...s, id: s.id.trim() })) })}
        >
          Create mission
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onDiscard}>
          Discard
        </Button>
      </div>
    </div>
  )
}
